import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { authApiClient, ApiError } from "@/api/authApiClient";
import { AlertsWrapper } from "@/components/AlertsWrapper";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/routes/paths";

interface ResetPasswordFields {
  password: string;
  passwordConfirmation: string;
}

export const ResetPassword = () => {
  const [serverErrors, setServerErrors] = useState<ApiError[]>([]);
  const [disabled, setDisabled] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ResetPasswordFields>({ mode: "onChange", disabled });

  const [searchParams] = useSearchParams();

  const onSubmit: SubmitHandler<ResetPasswordFields> = async (
    data: ResetPasswordFields,
  ) => {
    setDisabled(true);
    const resetToken = searchParams.get("reset_password_token") || "";
    const {
      success,
      data: responseData,
      errors,
    } = await authApiClient.changePassword(
      data["password"],
      data["passwordConfirmation"],
      resetToken,
    );
    if (success && responseData && responseData.message) {
      navigate(
        `/${ROUTES.PUBLIC.LOGIN}?message=${encodeURIComponent(responseData.message)}`,
      );
      return;
    }
    setServerErrors(errors);
    setDisabled(false);
  };

  const passwordsMatch = (_: string, fields: ResetPasswordFields) =>
    fields.password === fields.passwordConfirmation || t("login.match");

  return (
    <main role="main">
      <h2 className="mb-3 text-2xl font-medium text-foreground">
        {t("login.change")}
      </h2>
      <AlertsWrapper errors={serverErrors} messages={[]}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* New Password Field */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-foreground">
              {t("login.new")}
            </label>
            <input
              defaultValue=""
              {...register("password", {
                required: true,
                onChange: () => trigger(["passwordConfirmation"]),
              })}
              type="password"
              placeholder={t("login.new")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
              data-testid="password-input"
            />
          </div>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-foreground">
              {t("login.confirm")}
            </label>
            <input
              defaultValue=""
              {...register("passwordConfirmation", {
                required: true,
                validate: { passwordsMatch },
              })}
              type="password"
              placeholder={t("login.confirm")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
              data-testid="password-confirm-input"
            />
          </div>
          <div
            data-testid="validation-errors"
            className="mb-3 text-sm text-destructive"
          >
            {errors?.passwordConfirmation?.message}
          </div>
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed"
          >
            {isSubmitting ? t("login.loading") : t("login.update")}
          </button>
        </form>
      </AlertsWrapper>
      <div className="mt-4">
        <Link
          to="/login"
          className="inline-flex items-center text-sm font-medium text-foreground underline hover:no-underline"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t("login.back")}
        </Link>
      </div>
    </main>
  );
};
